import { createSignal, onCleanup } from "solid-js";
import { useWs } from "./ws";
import type { PluginJobEventPayload, WsIncoming } from "./types";
import { isWsEnvelope } from "./ws-protocol";

export type JobStatus = "pending" | "running" | "done" | "error";

export interface JobState {
  jobId: string;
  status: JobStatus;
  logs: string[];
  progress: number | null;
  result: unknown;
  error: string | null;
}

export interface JobEventFilterOptions {
  pluginId?: string | null | (() => string | null);
}

export function matchesPluginJobEvent(message: WsIncoming, jobId: string | null, pluginId?: string | null): boolean {
  if (!jobId || !isWsEnvelope(message, "live", "plugin", "job.event")) return false;
  const payload = (message as { payload?: { pluginId?: string; jobId?: string } }).payload;
  if (payload?.jobId !== jobId) return false;
  return !pluginId || payload.pluginId === pluginId;
}

function resolvePluginId(pluginId: JobEventFilterOptions["pluginId"]): string | null {
  return typeof pluginId === "function" ? pluginId() : (pluginId ?? null);
}

// Souscrit aux events job plugin envelope pour un jobId donné.
// Retourne un signal réactif sur l'état du job.
export function useJobEvents(jobId: () => string | null, options: JobEventFilterOptions = {}) {
  const ws = useWs();
  const [state, setState] = createSignal<JobState | null>(null);

  const off = ws.on(
    (m) => matchesPluginJobEvent(m, jobId(), resolvePluginId(options.pluginId)),
    (m) => {
      const evt = m as { payload?: { jobId?: string; event?: PluginJobEventPayload } };
      const p = evt.payload?.event;
      if (!evt.payload?.jobId || !p) return;
      setState((prev) => {
        const base: JobState = prev ?? {
          jobId: evt.payload!.jobId!,
          status: "running",
          logs: [],
          progress: null,
          result: null,
          error: null,
        };
        switch (p.type) {
          case "log":
            return { ...base, status: "running", logs: [...base.logs, p.line ?? ""] };
          case "progress":
            return {
              ...base,
              status: "running",
              progress: p.pct ?? null,
              logs: p.msg ? [...base.logs, p.msg] : base.logs,
            };
          case "done":
            return { ...base, status: "done", result: p.result ?? null };
          case "error":
            return { ...base, status: "error", error: p.error ?? "unknown" };
          default:
            return base;
        }
      });
    },
  );
  onCleanup(off);

  return state;
}
